import { useState } from "react";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Button, Card, CardMedia, Chip, Stack, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";

import awsLogo from "../../../../assets/aws-logo.svg";
import awsLogoDark from "../../../../assets/aws-logo-dark-mode.svg";
import Fsk8sPreviewDark from "../../../../assets/fsk8s/fsk8s-preview-dark.png";
import Fsk8sPreviewLight from "../../../../assets/fsk8s/fsk8s-preview-light.png";
import gcpLogo from "../../../../assets/gcp-logo.svg";
import { useDarkThemeCheck } from "../../../../Components/hooks/useDarkThemeCheck";
import { useIsFeatureEntitled } from "../../../../Context/TierProvider";
import { UpgradeOptions } from "../../../Upsell/UpgradeOptions";

const overviewBulletPoints = {
  "Cost visibility": "Track and monitor your Kubernetes spend",
  "Resource monitoring": "Track CPU and memory utilization",
  "Workload analysis": "Explore deployment performance",
  "Container-level view": "Analyze individual resource use",
};

const Overview = () => {
  const theme = useTheme();
  const isDarkTheme = useDarkThemeCheck();
  const customerHasEntitlement = useIsFeatureEntitled("lens:k8s");

  const [showUpgradeOptions, setShowUpgradeOptions] = useState(false);

  const handleUpgradeOptionsClick = () => {
    setShowUpgradeOptions(true);
    // TODO: Implement metrics
  };

  return (
    <>
      <Grid container spacing={6}>
        <Grid
          size={{
            sm: 12,
            md: 6,
          }}
        >
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography variant="h1">Improve stability and reduce waste in your Kubernetes environment</Typography>
              <Stack direction="row" spacing={1}>
                <Chip
                  size="small"
                  icon={
                    <img
                      src={isDarkTheme ? awsLogoDark : awsLogo}
                      width={18}
                      alt="AWS Logo"
                      style={{ margin: "0 2px 0 8px" }}
                    />
                  }
                  label="EKS available"
                  sx={{ backgroundColor: theme.palette.general.backgroundDark, color: theme.palette.text.primary }}
                />
                <Chip
                  size="small"
                  icon={<img src={gcpLogo} width={18} alt="GCP Logo" style={{ margin: "0 0 0 8px" }} />}
                  label="GKE available"
                  sx={{ backgroundColor: theme.palette.general.backgroundDark, color: theme.palette.text.primary }}
                />
              </Stack>
            </Stack>
            <Typography variant="body1">
              <span>
                Gain deep insights into your Kubernetes environment with granular analytics in the areas that matter
                most:
              </span>
              <ul>
                {Object.entries(overviewBulletPoints).map(([key, value]) => (
                  <li key={key}>
                    <span style={{ fontWeight: 500 }}>{key}:</span> {value}
                  </li>
                ))}
              </ul>
            </Typography>
            <Typography variant="body1">
              For an additional fee, you can also sign up for Flexsave for Kubernetes to start autonomously rightsizing
              your clusters for optimal performance at the lowest possible cost.
            </Typography>
            <Stack direction="row" spacing={2}>
              {!customerHasEntitlement && (
                <Button onClick={handleUpgradeOptionsClick} variant="contained" color="primary">
                  View upgrade options
                </Button>
              )}
              <Button
                variant="text"
                href="https://help.doit.com/docs/kubernetes"
                startIcon={<OpenInNewIcon />}
                target="_blank"
              >
                Learn more
              </Button>
            </Stack>
          </Stack>
        </Grid>
        <Grid
          size={{
            sm: 12,
            md: 6,
          }}
        >
          <Card
            sx={{
              width: "100%",
              height: "fit-content",
              minHeight: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "20px",
              boxShadow: 10,
            }}
          >
            <CardMedia
              component="img"
              image={isDarkTheme ? Fsk8sPreviewDark : Fsk8sPreviewLight}
              alt="Kubernetes Lens Preview"
              sx={{
                width: "100%",
                height: "auto",
              }}
            />
          </Card>
        </Grid>
      </Grid>
      <UpgradeOptions
        featureName="Kubernetes Lens"
        open={showUpgradeOptions}
        onClose={() => {
          setShowUpgradeOptions(false);
        }}
        sendUpsellEvent={() => {
          // TODO: Implement metrics
        }}
      />
    </>
  );
};

export default Overview;
